"use strict";

var _Object$defineProperty = require("@babel/runtime-corejs3/core-js-stable/object/define-property");
var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
_Object$defineProperty(exports, "__esModule", {
  value: true
});
exports.makeConfig = exports.machineJSON = exports.createApptInfoMachine = void 0;
var _find = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/instance/find"));
var _bind = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/instance/bind"));
var _xstate = require("xstate");
var _utils = require("../../lib/utils");
var _States = require("../../lib/constants/States");
var _Actions = require("../../lib/constants/Actions");
var _api = require("../../lib/api");
var _gxUtils = require("../../lib/gxUtils");
var _gx = require("../../lib/constants/gx");
const actions = {
  /**We set linked GX answers for use outside of apptinfo step.
   * Use cases include:
   *     - Some answers are bound for EHR
   *         - These are not to be shown again in the UI.
   *         - E.g., telehealth attestation is asked once by GX, and then the answer is passed to EHR.
   *     - Controls that have a `path` variable may be able to be linked to other parts of the ui.
   *         - E.g., An answer from GX for DOB could be used to auto-fill DOB in the patient reg form.
   */
  setLinkedGxAnswers: (0, _xstate.assign)({
    linkedGxAnswers: (ctx, ev) => {
      const {
        steps
      } = ev.data;
      const linkedGxAnswers = ctx.linkedGxAnswers;
      const updatedLinkedGxAnswers = (0, _gxUtils.getUpdatedLinkedGXAnswers)(steps, linkedGxAnswers);
      return updatedLinkedGxAnswers;
    }
  }),
  setPatientRel: (0, _xstate.assign)({
    patientRel: (ctx, ev) => ev.data.patientRel,
    purpose: () => null
  }),
  setApptPurpose: (0, _xstate.assign)({
    purpose: (ctx, ev) => ev.data.purpose
  }),
  sendParentNext: (0, _xstate.sendParent)(ctx => ({
    type: _Actions.NEXT,
    data: {
      patientRel: ctx.patientRel,
      purpose: ctx.purpose,
      isCaptchaVerified: ctx.isCaptchaVerified,
      linkedGxAnswers: ctx.linkedGxAnswers
    }
  })),
  setIsCaptchaVerified: (0, _xstate.assign)((ctx, ev) => ({
    isCaptchaVerified: ev.data
  })),
  deletePurposeFromCtx: (0, _xstate.assign)(() => ({
    purpose: null
  })),
  setConditionalGxQuestions: (0, _xstate.assign)((ctx, ev) => ({
    gx: {
      ...ctx.gx,
      v2Conditionals: ev.data
    }
  })),
  clearConditionalGxQuestions: (0, _xstate.assign)(ctx => ({
    gx: {
      ...ctx.gx,
      v2Conditionals: null
    }
  })),
  /** Setting the context from which the GX backend will read and respond with relevant GX questons
   * - `current_section: 'conditional'` - a flag that notifies the GX backend we want conditional GX questions (vs non-conditional questions)
   * - `visit_method` - this is passed today as the only condition the GX backend checks on whether to return conditional GX questions (e.g. telehealth attestation feature)
   * NOTE: In the future, we may want to pass in more context (such as patient relationship) which could trigger different conditional GX questions
   */
  setConditionalGxContext: (0, _xstate.assign)({
    gx: ctx => {
      const gxVisitMethod = ctx.purpose?.visit_method ?? null;
      const conditionalGXContext = {
        current_section: _gx.GX_CONTEXT.CURRENT_SECTION.CONDITIONAL,
        visit_method: gxVisitMethod
      };
      const contextualInfo = {
        context: {
          ...ctx.gx.contextualInfo?.context,
          ...conditionalGXContext
        }
      };
      return {
        ...ctx.gx,
        contextualInfo
      };
    }
  }),
  /** clear the flag used to check if a call was made to GX API to check for conditional questions using relevant and up to date contextualInfo */
  clearGxConditionalContextualInfoSent: (0, _xstate.assign)({
    gx: ctx => ({
      ...ctx.gx,
      conditionalContextualInfoSent: false
    })
  })
};

/**
 * @param {machine.context} ctx
 * @returns {Boolean}
 */
const ctxPurposeExists = ctx => {
  var _context, _context2;
  const {
    patientRel,
    purposeOptionsByRel
  } = ctx;
  if (!ctx.purpose) {
    return false;
  }
  const purposeExists = ((_context = purposeOptionsByRel[patientRel]) == null ? void 0 : (0, _bind.default)(_context2 = Function.call).call(_context2, (0, _find.default)(_context), _context))?.(v => v.name === ctx.purpose.name);
  return Boolean(purposeExists);
};
const guards = {
  doesNotHavePurposes: ctx => ctx.purposeOptionsByRel[ctx.patientRel].length === 0,
  hasNoAvailability: ctx => {
    const {
      purposesWithAvailability
    } = ctx;
    for (const key in purposesWithAvailability) {
      if (purposesWithAvailability[key].size) {
        return false;
      }
    }
    return true;
  },
  hasPatientRelAvailability: ctx => {
    const {
      purposesWithAvailability,
      patientRel
    } = ctx;
    return purposesWithAvailability[patientRel].size > 0;
  },
  hasPurposeAvailability: ctx => {
    const {
      purpose,
      purposesWithAvailability,
      patientRel
    } = ctx;
    return purposesWithAvailability[patientRel].has(purpose.name);
  },
  hasCtxPurposeWithAvailability: ctx => {
    const {
      patientRel,
      purpose,
      purposesWithAvailability
    } = ctx;
    const purposeExists = ctxPurposeExists(ctx);
    return purposeExists && purposesWithAvailability[patientRel].has(purpose.name);
  },
  hasCtxPurposeWithNoAvailability: ctx => {
    const {
      patientRel,
      purpose,
      purposesWithAvailability
    } = ctx;
    const purposeExists = ctxPurposeExists(ctx);
    return purposeExists && !purposesWithAvailability[patientRel].has(purpose.name);
  },
  hasGxQuestions: ctx => Boolean(ctx.gx.v1),
  hasGxV2Questions: ctx => Boolean(ctx.gx.v2),
  hasGxConditionalQuestions: ctx => Boolean(ctx.gx.v2Conditionals),
  isCaptchaNotVerifiedSession: (_ctx, ev) => {
    return !ev.data;
  },
  isCaptchaVerifiedCtx: ctx => {
    return Boolean(ctx.isCaptchaVerified);
  },
  hasPatientRelInCtx: ctx => !!ctx.patientRel,
  hasNoConditionalGxQuestions: (ctx, ev) => {
    return !ev.data;
  },
  hasPatientRelAndPurposeWithAvailability: ctx => {
    const {
      patientRel,
      purpose,
      purposesWithAvailability
    } = ctx;
    const purposeExists = ctxPurposeExists(ctx);
    return patientRel && purposeExists && purposesWithAvailability[patientRel].has(purpose.name);
  },
  conditionalGxContextualInfoNotSent: ctx => {
    return !ctx.gx.conditionalContextualInfoSent;
  }
};
const services = {
  getConditionalGxStepByProviderId: async ctx => {
    const api = new _api.Api(ctx.customerCode);
    return await api.getGxStepByProviderId(ctx.provider.id, ctx.gx.contextualInfo);
  },
  checkCaptchaVerified: async ctx => {
    const api = new _api.Api(ctx.customerCode);
    const res = await api.checkCaptcha();
    return res.verified;
  }
};

// This is exposed for snapshot testing
const machineJSON = _ref => {
  let {
    provider,
    isCaptchaVerified,
    csrfToken,
    patientRel,
    purpose,
    appointmentQuery,
    gx,
    hidePatientRelConfig,
    services,
    visibility
  } = _ref;
  return {
    id: `Machine__${_States.apptInfo}`,
    type: 'parallel',
    context: {
      purposesWithAvailability: provider.availableApptPurposes,
      purposeOptionsByRel: (0, _utils.getPurposeOptionsByPatientRel)(provider, visibility.schedulingVisibility),
      provider,
      isCaptchaVerified,
      patientRel: appointmentQuery.patientRel ? appointmentQuery.patientRel : patientRel,
      purpose: appointmentQuery.purpose ? appointmentQuery.purpose : purpose,
      purposeTerms: [],
      csrfToken,
      visibility,
      gx,
      linkedGxAnswers: {},
      hidePatientRel: (0, _utils.hidePatientRel)(provider, hidePatientRelConfig),
      services
    },
    states: {
      // Gx controlled specific questions
      [_States.gxQuestions]: {
        initial: _States.routing,
        states: {
          [_States.routing]: {
            always: [{
              cond: 'hasNoAvailability',
              target: _States.noAvailability
            }, {
              cond: 'hasGxQuestions',
              target: _States.apptGxQuestions
            }, {
              cond: 'hasGxV2Questions',
              target: _States.apptGxV2Questions
            }, {
              target: _States.noResults
            }]
          },
          [_States.noAvailability]: {},
          [_States.noResults]: {},
          // gxQuestions.apptGxQuestions
          [_States.apptGxQuestions]: {
            initial: _States.idle,
            on: {
              [_Actions.GX_QUESTION_ANSWERED]: `.${_States.idle}`,
              [_Actions.GX_PASSED]: `.${_States.passedGx}`,
              [_Actions.GX_V1_FAILED]: `.${_States.failedGxV1}`
            },
            states: {
              [_States.idle]: {},
              [_States.passedGx]: {},
              [_States.failedGxV1]: {}
            }
          },
          // gxQuestions.apptGxV2Questions
          [_States.apptGxV2Questions]: {
            initial: _States.idle,
            on: {
              [_Actions.GX_PASSED]: {
                actions: ['setLinkedGxAnswers']
              }
            },
            states: {
              [_States.idle]: {}
            }
          }
        }
      },
      // patient relationship, appointment purpose, and conditional Gx questions; recaptcha
      [_States.apptQuestions]: {
        initial: _States.routing,
        on: {
          [_Actions.SET_PATIENT_REL]: {
            target: `.${_States.apptPatientRel}.${_States.routing}`,
            actions: 'setPatientRel'
          },
          [_Actions.SET_APPT_PURPOSE]: {
            target: `.${_States.apptPurpose}.${_States.routing}`,
            actions: ['setApptPurpose',
            // a purpose selection will update the conditions that conditional Gx questions toggles on
            'clearGxConditionalContextualInfoSent']
          },
          // if a user changes their Gx answer,
          // then we'll go back to an idle state to wait for a "passed" gx validation
          [_Actions.GX_QUESTION_ANSWERED]: `.${_States.idle}`,
          // GX v1's `onChange` is only called up until a GX v1 question set has been completed,
          // then `onDone` is be called with any user answer change after that.
          // Therefore, this event listener remains here until GX v1 retires to the white sands of Ipanema Beach.
          [_Actions.GX_V1_FAILED]: `.${_States.idle}`
        },
        states: {
          [_States.routing]: {
            always: [{
              cond: 'hasNoAvailability',
              target: _States.apptPatientRel
            }, {
              cond: 'hasGxQuestions',
              target: _States.idle
            }, {
              cond: 'hasGxV2Questions',
              target: _States.idle
            }, {
              // cond may be true w/ deepLinking
              cond: 'hasPatientRelAndPurposeWithAvailability',
              target: _States.conditionalGxQuestions
            }, {
              target: _States.apptPatientRel
            }]
          },
          // apptQuestions.idle
          [_States.idle]: {
            on: {
              [_Actions.GX_PASSED]: _States.apptPatientRel
            }
          },
          // apptQuestions.apptPatientRel
          [_States.apptPatientRel]: {
            initial: _States.idle,
            states: {
              [_States.idle]: {
                always: [{
                  cond: 'hasPatientRelInCtx',
                  target: _States.routing
                }]
              },
              [_States.noAvailability]: {},
              [_States.routing]: {
                always: [{
                  cond: 'hasPatientRelAvailability',
                  target: _States.done
                }, {
                  target: _States.noAvailability
                }]
              },
              [_States.done]: {
                type: 'final'
              }
            },
            onDone: _States.apptPurpose
          },
          // apptQuestions.apptPurpose
          [_States.apptPurpose]: {
            initial: _States.pending,
            states: {
              [_States.routing]: {
                always: [{
                  cond: 'hasPurposeAvailability',
                  target: _States.done
                }, {
                  target: _States.noAvailability
                }]
              },
              [_States.noAvailability]: {},
              [_States.idle]: {},
              [_States.noResults]: {},
              [_States.pending]: {
                always: [{
                  cond: 'doesNotHavePurposes',
                  target: _States.noResults
                }, {
                  // we have a `purpose` with availability in `ctx`, because the user
                  // selected it or it was passed by deepLinking
                  cond: 'hasCtxPurposeWithAvailability',
                  target: _States.done
                }, {
                  cond: 'hasCtxPurposeWithNoAvailability',
                  target: _States.noAvailability
                }, {
                  target: _States.idle,
                  actions: 'deletePurposeFromCtx'
                }]
              },
              [_States.done]: {
                type: 'final'
              }
            },
            onDone: {
              target: _States.conditionalGxQuestions
            }
          },
          // apptQuestions.conditionalGxQuestions
          [_States.conditionalGxQuestions]: {
            initial: _States.routing,
            states: {
              [_States.routing]: {
                always: [
                // this condition is true for the following cases:
                // 1) non-deepLinked scenario
                //     - need to make gx conditional call
                // 2) deepLinked scenario #1: the user selected a new purpose
                //     - need to make gx conditional call
                {
                  cond: 'conditionalGxContextualInfoNotSent',
                  target: _States.pending
                },
                // deepLinked scenario #2: already conditionalContextualInfoSent and got conditional questions
                //     - no need to make gx conditional call again
                {
                  cond: 'hasGxConditionalQuestions',
                  target: _States.conditionalApptGxV2Questions
                },
                // deepLinked scenario #3: conditionalContextualInfoSent but no conditional questions
                //     - no need to make gx conditional call again nor render conditionals
                {
                  target: _States.done
                }]
              },
              [_States.pending]: {
                entry: 'setConditionalGxContext',
                invoke: {
                  id: 'getConditionalGxStepByProviderId',
                  src: 'getConditionalGxStepByProviderId',
                  onDone: [{
                    cond: 'hasNoConditionalGxQuestions',
                    actions: 'clearConditionalGxQuestions',
                    target: _States.done
                  }, {
                    actions: 'setConditionalGxQuestions',
                    target: _States.conditionalApptGxV2Questions
                  }],
                  onError: _States.error
                }
              },
              [_States.conditionalApptGxV2Questions]: {
                initial: _States.idle,
                on: {
                  [_Actions.CONDITIONAL_GX_PASSED]: {
                    target: `.${_States.passedGx}`,
                    actions: ['setLinkedGxAnswers']
                  }
                },
                states: {
                  [_States.idle]: {},
                  [_States.passedGx]: {
                    type: 'final'
                  }
                },
                onDone: {
                  target: _States.done
                }
              },
              [_States.error]: {},
              [_States.done]: {
                type: 'final'
              }
            },
            onDone: {
              target: _States.apptInfoComplete
            }
          },
          // apptQuestions.apptInfoComplete
          [_States.apptInfoComplete]: {
            initial: _States.idle,
            on: {
              [_Actions.CONDITIONAL_GX_QUESTION_ANSWERED]: `${_States.conditionalGxQuestions}.${_States.conditionalApptGxV2Questions}`
            },
            states: {
              [_States.idle]: {
                on: {
                  [_Actions.NEXT]: [
                  /**
                   * if navigating back after having already verified captcha,
                   * then the verified flag will be in ctx
                   */
                  {
                    cond: 'isCaptchaVerifiedCtx',
                    actions: 'sendParentNext'
                  }, {
                    target: _States.checkingCaptchaSession
                  }]
                }
              },
              [_States.checkingCaptchaSession]: {
                invoke: {
                  id: 'checkingCaptchaVerified',
                  src: 'checkCaptchaVerified',
                  onDone: [{
                    cond: 'isCaptchaNotVerifiedSession',
                    target: _States.verifyingCaptcha
                  }, {
                    actions: ['setIsCaptchaVerified', 'sendParentNext']
                  }],
                  onError: _States.verifyingCaptcha
                }
              },
              [_States.verifyingCaptcha]: {
                on: {
                  [_Actions.CAPTCHA_CANCELLED]: {
                    target: _States.idle
                  },
                  [_Actions.CAPTCHA_VERIFIED]: {
                    actions: ['setIsCaptchaVerified', 'sendParentNext'],
                    target: _States.idle
                  },
                  [_Actions.CAPTCHA_ERROR]: {
                    target: _States.verifyingCaptchaError
                  }
                }
              },
              [_States.verifyingCaptchaError]: {
                on: {
                  [_Actions.CAPTCHA_RESET]: {
                    target: _States.checkingCaptchaSession
                  },
                  [_Actions.CAPTCHA_CANCELLED]: {
                    target: _States.idle
                  }
                }
              }
            }
          }
        }
      }
    }
  };
};
exports.machineJSON = machineJSON;
const makeConfig = () => ({
  services,
  actions,
  guards
});
exports.makeConfig = makeConfig;
const createApptInfoMachine = _ref2 => {
  let {
    config = makeConfig(),
    ...props
  } = _ref2;
  return (0, _xstate.Machine)(machineJSON(props)).withConfig(config);
};
exports.createApptInfoMachine = createApptInfoMachine;